import React, { createContext, Dispatch, ReactNode, useContext, useEffect, useReducer } from "react";
import { FullPageLoader } from "../loaders/FullPageLoader";
import { loadReports } from "../loaders/report-entry-loaders";
import { Account, monthGoal } from "../models/Account";
import { cpl, FullReportEntry, gap } from "../models/FullReportEntry";
import { getRegion } from "../utils/state-utils";
import { EnumChecker } from "../utils/utils";
import { filterReducer, FilterState, IFilter } from "./filtering/filters";
import { FilterAction } from "./filtering/FilterAction";
import { AccountSorter, createBalanceSorter, createNumberSorter, createNumberFnSorter, createStrSorter, SorterId, createSorter, createLeadGainSorter } from "./sorting/account-sorting";
import { TagContext } from "../tags/tag-context";
import { listTags } from "../tags/tags-api";
import { asIdSet } from "../components/utils";
import { useIsAwr } from "../login/AuthContext";



export interface AccountAction {
    type: "setPeriod" | "selectAccount" | "setTableMode" | "setData" | "setFiltered" | "setLoading" | "setGrouping" | "setShowPrevious" | "setDetailInfoMode" | 
    "setFilter" | "setSorter" | "invertSort" | "setHiddenColumn" | "setPastRender" | "setChannelMode" | "setGoalMode" | "updateTags" | FilterAction;
    payload?: any
}

export interface AccountContextState {
    account?: FullReportEntry
    accounts?: FullReportEntry[] 
    filtered?: FullReportEntry[] 
    loading?: boolean 
    groupingOn?: boolean
    showPrevious?: boolean
    detailInfoMode?: "lead" | "cost"
    sorters?: AccountSorter[]
    filters?: FilterState
    currentSorter?: SorterId
    period?: string
    customGoalMode?: boolean
    tableMode?: boolean | "simple"
    channelMode?: boolean
    pastRender?: boolean
    hiddenColumns?: string[]
    googleMode?: boolean
    dispatch?:Dispatch<AccountAction>
}

const isFilterAction = EnumChecker(FilterAction);

function toggleSortOrder(state: AccountContextState):AccountContextState{
    if(state.currentSorter){
        return {...state, currentSorter: {...state.currentSorter, order: state.currentSorter.order == 'asc' ? 'desc' : 'asc'}}
    }

    return state;
}

function updateTags(accounts:FullReportEntry[], account:number, tags:number[]){
    var act = accounts.find(a => a.id == account);
    act = {...act, tags};

    const list = accounts.map(a => a.id != account ? a : act);
    return  {accounts: list, account: act}
}

export function reducer(state: AccountContextState, action: AccountAction):AccountContextState{
    if(isFilterAction(action.type)){
        return {...state, filters: filterReducer(state.filters || {}, action)}
    }

    switch(action.type){
        case "setLoading": return {...state, loading: action.payload}
        case "setPeriod": return {...state, period: action.payload}
        case "setGoalMode": return {...state, customGoalMode: action.payload}
        case "selectAccount": return {...state, account: action.payload}
        case "setData": return {...state, accounts: action.payload}
        case "setFiltered": return {...state, filtered: action.payload}
        case "setDetailInfoMode": return {...state, detailInfoMode: action.payload}
        case "setSorter": return {...state, currentSorter: action.payload}
        case "invertSort": return toggleSortOrder(state)
        case "setGrouping": return {...state, groupingOn: action.payload}
        case "setShowPrevious": return {...state, showPrevious: action.payload}
        case "setTableMode": return {...state, tableMode: action.payload}
        case "setChannelMode": return {...state, channelMode: action.payload}
        case "setPastRender": return {...state, pastRender: action.payload}
        case "setHiddenColumn": return {...state, hiddenColumns: action.payload}
        case "updateTags": return {...state, ...updateTags(state.accounts, action.payload.account, action.payload.tags)}
        
        default: return state
    }
}

export const AccountContext = createContext<AccountContextState>({});

function buildBaseState(){
    const awr = useIsAwr()
    const googleMode = (document.querySelector('#root') as any).dataset.mode == 'google';

    const sorters = [
        createStrSorter("name", "Nome da Conta"),
        createStrSorter("manager", "Gestor"),
        createStrSorter("consultant", "Consultor"),
        createNumberSorter("status", "Status da Conta"),
        createNumberFnSorter(monthGoal, "Meta Geral"),
        createNumberSorter("goalRatio", "Meta Proporcional"),
        createNumberSorter("nLeads", "Leads no mês"),
        createNumberSorter("todayLeads", "Leads de Hoje"),
        createNumberSorter("clicks", "Clicks"),
        createStrSorter("trend", "Tendência"),
        createNumberSorter("budget", "Orçamento"),
        createNumberSorter("overrun", "Investimento / Orçamento"),
        createBalanceSorter("Saldo"),
        createLeadGainSorter("Leads / Mês Passado"),
        createSorter("Custo por lead", (a, b) => cpl(a) - cpl(b)),
        createSorter("Gap atual", (a, b) => gap(a) - gap(b)),
        createNumberSorter("missingDays", "Dias Faltando"),
        createNumberSorter("score", "Otimização"),
        createSorter("Comparativo Leads", (a, b) => (a.nLeads - a.pastMonthSameDay.leads) - (b.nLeads - b.pastMonthSameDay.leads)),
        createSorter("Comparativo Investimento", (a, b) => (a.totalCost - a.pastMonthSameDay.cost) - (b.totalCost - b.pastMonthSameDay.cost)),
        
        ... awr && !googleMode ? 
        [createSorter("Orçamento Google", (a, b) => (a.info?.googleBudget || 0) - (b.info?.googleBudget || 0)),
        createSorter("Orçamento Total", (a, b) => a.budget + (a.info?.googleBudget || 0) - (b.budget + (b.info?.googleBudget || 0)))] :
        []
    ]

    

    return {loading: true, groupingOn: true, sorters, googleMode, hiddenColumns: ["leadGain", 'consultant']};
}

export function AccountStore({children}:{children: ReactNode}){
    const [state, dispatch] = useReducer(reducer, {}, buildBaseState);
    const tagContext = useContext(TagContext)
    
    useEffect(function(){
        (async function() {
            try{
                dispatch({type:"setLoading", payload: true});
                const accounts = await loadReports(state.period, state.googleMode, state.customGoalMode ?? false);
                accounts.sort((a, b) => b.name.localeCompare(b.name))
                accounts.sort((a, b) => getRegion(b.state).localeCompare(getRegion(a.state)))
                const tags = await listTags(asIdSet(accounts, a => a.franchise))
                tagContext.dispatch({type: "setTags", tags})
                dispatch({type:"setData", payload: accounts});
            } catch(ex){
                console.log(ex);
            }
            dispatch({type:"setLoading", payload: false});
        })()
    }, [state.period, state.customGoalMode]);

    if(state.loading)
        return <FullPageLoader />

    return <AccountContext.Provider value={{...state, dispatch}}>
        {children}
    </AccountContext.Provider>
}

export function useShowPrevious(){
    const {showPrevious} = useContext(AccountContext);
    return showPrevious;
}

export function useTableMode(){
    const {tableMode} = useContext(AccountContext);
    return tableMode;
}

export function usePastRender(){
    const {pastRender} = useContext(AccountContext);
    return pastRender;
}

export function useGoogleMode(){
    const {googleMode} = useContext(AccountContext);
    return googleMode;
}